import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchAttendance,
  deleteAttendance,
} from "../redux/actions/attendanceActions";
import {
  fetchDistricts,
  fetchMunicipalities,
} from "../redux/actions/locationActions";
import { fetchDesignations } from "../redux/actions/designationActions";
import theme from "../components/shared/theme";
import {
  tdStyle,
  thStyle,
  paginationButtonStyle,
  dropdownStyle,
} from "../components/shared/themeStyles";
import ExcelJS from "exceljs";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Attendance = () => {
  const dispatch = useDispatch();

  const { attendance, loading, error, pagination } = useSelector(
    (state) => state.attendance
  );
  const { districts = [], municipalities = [] } = useSelector(
    (state) => state.location || {}
  );
  const { designations = [] } = useSelector(
    (state) => state.designation || {}
  );

  const [filters, setFilters] = useState({
    search: "",
    fromDate: "",
    toDate: "",
    district: "",
    municipality: "",
    designation: "",
    status: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [downloadingExcel, setDownloadingExcel] = useState(false);

  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [photoUrl, setPhotoUrl] = useState("");
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [mapAddress, setMapAddress] = useState("Loading...");
  const [mapLoading, setMapLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const itemsPerPage = 10;
  const GOOGLE_MAPS_KEY = process.env.REACT_APP_MAP_API_KEY;

  const debounceRef = useRef(null);

  const selectedDistrictObj = districts.find((d) => d.name === filters.district);
  const selectedDistrictId = selectedDistrictObj?.id;
  const filteredMunicipalities = selectedDistrictId
    ? municipalities.filter((m) => m.district_id === selectedDistrictId)
    : municipalities;

  useEffect(() => {
    setFilters((prev) => ({ ...prev, municipality: "" }));
  }, [filters.district]);

  const fetchData = useCallback(
    (page) => {
      dispatch(
        fetchAttendance({
          page,
          limit: itemsPerPage,
          district: filters.district,
          municipality: filters.municipality,
          fromDate: filters.fromDate,
          toDate: filters.toDate,
          search: filters.search,
          designation: filters.designation,
          attendance_status: filters.status,
        })
      );
    },
    [dispatch, filters, itemsPerPage]
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchData(currentPage);
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [filters, currentPage, fetchData]);

  useEffect(() => {
    dispatch(fetchDistricts());
    dispatch(fetchMunicipalities());
    dispatch(fetchDesignations());
  }, [dispatch]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({
      search: "",
      fromDate: "",
      toDate: "",
      district: "",
      municipality: "",
      designation: "",
      status: "",
    });
    setCurrentPage(1);
  };

  const openPhotoModal = (url) => {
    setPhotoUrl(url);
    setPhotoModalOpen(true);
  };
  const closePhotoModal = () => {
    setPhotoModalOpen(false);
    setPhotoUrl("");
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setConfirmDeleteOpen(true);
  };
  const confirmDelete = () => {
    dispatch(deleteAttendance(deleteId));
    setConfirmDeleteOpen(false);
  };

  const openMapModal = async (lat, lng) => {
    setMapModalOpen(true);
    setMapLoading(true);
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_KEY}`
      );
      const data = await response.json();
      if (data.status === "OK") {
        setMapAddress(data.results[0]?.formatted_address || "No address found");
      } else {
        setMapAddress("Unable to fetch address");
      }
    } catch (err) {
      setMapAddress("Error fetching address");
    } finally {
      setMapLoading(false);
    }
  };
  const closeMapModal = () => {
    setMapModalOpen(false);
    setMapAddress("");
  };

  const getStatusStyle = (status) => {
    if (!status) return {};
    const s = status.toLowerCase();
    if (s === "present") return { color: "#2e7d32", fontWeight: "bold" };
    if (s === "absent") return { color: "#c62828", fontWeight: "bold" };
    if (s === "late") return { color: "#ed6c02", fontWeight: "bold" };
    if (s === "early departure") return { color: "#9c27b0", fontWeight: "bold" };
    return {};
  };

  // Excel export (updated to 12 columns instead of 14)
  const handleDownloadExcel = async () => {
    if (downloadingExcel) return;
    setDownloadingExcel(true);
    try {
      const token = localStorage.getItem("token");
      const queryParams = new URLSearchParams({
        page: 1,
        limit: 100000,
        district: filters.district,
        municipality: filters.municipality,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        search: filters.search,
        designation: filters.designation,
        attendance_status: filters.status,
      }).toString();

      const response = await fetch(
        `${BASE_URL}/v1/api/admin/getAllAttendance?${queryParams}`,
        { headers: { authorization: token } }
      );
      if (!response.ok) throw new Error("Failed to fetch data");
      const data = await response.json();
      const records = data?.data || [];

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Attendance", {
        views: [{ state: "frozen", ySplit: 1 }],
      });

      // Define columns – removed Late In and Early Out
      worksheet.columns = [
        { header: "ID", key: "id", width: 8 },
        { header: "Date", key: "date", width: 12 },
        { header: "District", key: "district", width: 14 },
        { header: "Municipality", key: "municipality", width: 16 },
        { header: "Designation", key: "designation", width: 18 },
        { header: "Employee Name", key: "employee_name", width: 22 },
        { header: "Phone", key: "employee_phone", width: 14 },
        { header: "Check In", key: "check_in", width: 12 },
        { header: "Check Out", key: "check_out", width: 12 },
        { header: "Status", key: "attendance_status", width: 14 },
        { header: "Remarks", key: "remarks", width: 12 },
      ];

      // Style header
      worksheet.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
      worksheet.getRow(1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF333333" },
      };
      worksheet.getRow(1).alignment = { horizontal: "center", vertical: "middle" };

      // Add data
      records.forEach((rec) => {
        const row = worksheet.addRow({
          id: rec.id,
          date: rec.date,
          district: rec.district,
          municipality: rec.municipality,
          designation: rec.designation,
          employee_name: rec.employee_name,
          employee_phone: rec.employee_phone,
          check_in: rec.check_in || "",
          check_out: rec.check_out || "",
          attendance_status: rec.attendance_status || "",
          remarks: rec.remarks || "",
        });

        // Color cells based on attendance_status
        const status = rec.attendance_status?.toLowerCase();
        if (status) {
          const statusCell = row.getCell("attendance_status");
          if (status === "present") {
            statusCell.font = { color: { argb: "FF2E7D32" }, bold: true };
          } else if (status === "absent") {
            statusCell.font = { color: { argb: "FFC62828" }, bold: true };
          } else if (status === "late") {
            statusCell.font = { color: { argb: "FFED6C02" }, bold: true };
          } else if (status === "early departure") {
            statusCell.font = { color: { argb: "FF9C27B0" }, bold: true };
          }
        }

        row.alignment = { vertical: "middle" };
      });

      // Auto filter
      worksheet.autoFilter = {
        from: "A1",
        to: `K${worksheet.rowCount}`, // 11 columns
      };

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Attendance_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Excel download error:", err);
      alert("Failed to download Excel. Please try again.");
    } finally {
      setDownloadingExcel(false);
    }
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString("en-IN");
  const totalColumns = 14; // updated (ID, Date, District, Municipality, Designation, Name, Phone, In/Out, In Addr, Out Addr, Photo, Status, Remarks, Delete)

  return (
    <div style={{ padding: theme.spacing.lg, backgroundColor: theme.colors.background }}>
      <h1 style={{
        fontSize: theme.font.size["3xl"],
        fontWeight: theme.font.weight.bold,
        color: theme.colors.grayDark,
        marginBottom: theme.spacing.lg,
      }}>
        Attendance Records
      </h1>

      {/* Filters */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: theme.spacing.sm, marginBottom: theme.spacing.lg,
        backgroundColor: "#fff", padding: theme.spacing.md, borderRadius: theme.radius.xl, boxShadow: theme.shadow.soft,
      }}>
        <select value={filters.district} onChange={(e) => updateFilter("district", e.target.value)} style={dropdownStyle}>
          <option value="">All Districts</option>
          {districts.map(d => (
            <option key={d.id} value={d.name}>{d.name}</option>
          ))}
        </select>

        <select value={filters.municipality} onChange={(e) => updateFilter("municipality", e.target.value)} style={dropdownStyle}>
          <option value="">All Municipalities</option>
          {filteredMunicipalities.map(m => (
            <option key={m.id} value={m.name}>{m.name}</option>
          ))}
        </select>

        <select value={filters.designation} onChange={(e) => updateFilter("designation", e.target.value)} style={dropdownStyle}>
          <option value="">All Designations</option>
          {designations.map((d, idx) => (
            <option key={idx} value={d}>{d}</option>
          ))}
        </select>

        <select value={filters.status} onChange={(e) => updateFilter("status", e.target.value)} style={dropdownStyle}>
          <option value="">All Statuses</option>
          <option value="Present">Present</option>
          <option value="Absent">Absent</option>
          <option value="Late">Late</option>
          <option value="Early Departure">Early Departure</option>
        </select>

        <input
          type="text"
          placeholder="Search name, phone, municipality, district"
          value={filters.search}
          onChange={(e) => updateFilter("search", e.target.value)}
          style={{ ...dropdownStyle, minWidth: "220px" }}
        />
        <input type="date" value={filters.fromDate} onChange={(e) => updateFilter("fromDate", e.target.value)} style={dropdownStyle} />
        <input type="date" value={filters.toDate} onChange={(e) => updateFilter("toDate", e.target.value)} style={dropdownStyle} />
        <button onClick={resetFilters} style={{ backgroundColor: theme.colors.primary, color: "#fff", padding: theme.spacing.sm, borderRadius: theme.radius.md }}>
          Reset
        </button>
        <button
          onClick={handleDownloadExcel}
          disabled={downloadingExcel}
          style={{
            backgroundColor: downloadingExcel ? "#aaa" : "#28a745",
            color: "#fff",
            padding: theme.spacing.sm,
            borderRadius: theme.radius.md,
            border: "none",
            cursor: downloadingExcel ? "not-allowed" : "pointer",
            fontWeight: "bold",
            minWidth: "100px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
          }}
        >
          {downloadingExcel ? (
            <>
              <div className="spinner-small" /> Excel...
            </>
          ) : (
            <>
              <span>📊</span> Excel
            </>
          )}
        </button>
      </div>

      {/* Downloading overlay */}
      {downloadingExcel && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(255,255,255,0.85)", display: "flex",
          flexDirection: "column", alignItems: "center", justifyContent: "center",
          zIndex: 9999,
        }}>
          <div className="spinner" />
          <p style={{ marginTop: theme.spacing.md, fontSize: theme.font.size.md, fontWeight: "bold", color: theme.colors.grayDark }}>
            Generating Excel... Please wait
          </p>
        </div>
      )}

      {/* Table with loading overlay */}
      <div style={{
        backgroundColor: "#fff",
        borderRadius: theme.radius.xl,
        boxShadow: theme.shadow.soft,
        overflowX: "auto",
        maxWidth: "100%",
        position: "relative",
      }}>
        {loading && (
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: "rgba(255,255,255,0.7)", display: "flex",
            alignItems: "center", justifyContent: "center", zIndex: 10,
            borderRadius: theme.radius.xl,
          }}>
            <div className="spinner" />
          </div>
        )}

        <table style={{
          width: "100%",
          borderCollapse: "collapse",
          minWidth: "1000px",
          fontSize: "0.75rem",
        }}>
          <thead style={{ backgroundColor: theme.colors.grayDark, color: "#fff" }}>
            <tr>
              <th style={{...thStyle, width: "4%"}}>ID</th>
              <th style={{...thStyle, width: "8%"}}>Date</th>
              <th style={{...thStyle, width: "8%"}}>District</th>
              <th style={{...thStyle, width: "9%"}}>Municipality</th>
              <th style={{...thStyle, width: "9%"}}>Designation</th>
              <th style={{...thStyle, width: "10%"}}>Name</th>
              <th style={{...thStyle, width: "8%"}}>Phone</th>
              <th style={{...thStyle, width: "8%"}}>In/Out</th>
              {/* Late In and Early Out removed */}
              <th style={{...thStyle, width: "8%"}}>In Addr</th>
              <th style={{...thStyle, width: "8%"}}>Out Addr</th>
              <th style={{...thStyle, width: "5%"}}>Photo</th>
              <th style={{...thStyle, width: "8%"}}>Status</th>
              <th style={{...thStyle, width: "8%"}}>Remarks</th>
              <th style={{...thStyle, width: "5%"}}>Delete</th>
            </tr>
          </thead>
          <tbody>
            {error ? (
              <tr><td colSpan={totalColumns} style={{ textAlign: "center", padding: "20px", color: "red" }}>{error}</td></tr>
            ) : attendance.length === 0 && !loading ? (
              <tr><td colSpan={totalColumns} style={{ textAlign: "center", padding: "20px" }}>No records found</td></tr>
            ) : (
              attendance.map((att) => {
                const isAbsent = (att.attendance_status || "").toLowerCase() === "absent";

                return (
                  <tr key={att.id} style={{ borderBottom: "1px solid #eee" }}>
                    <td style={tdStyle}>{att.id}</td>
                    <td style={tdStyle}>{formatDate(att.date)}</td>
                    <td style={tdStyle}>{att.district}</td>
                    <td style={tdStyle}>{att.municipality}</td>
                    <td style={tdStyle}>{att.designation}</td>
                    <td style={tdStyle}>{att.employee_name}</td>
                    <td style={tdStyle}>{att.employee_phone}</td>

                    {/* In / Out */}
                    <td style={tdStyle}>
                      {isAbsent ? (
                        <div style={{ display: "flex", flexDirection: "column", color: "#9e9e9e" }}>
                          <span>In: {att.check_in || "-"}</span>
                          <span>Out: {att.check_out || "-"}</span>
                        </div>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <span style={{ color: "#10b981" }}>In: {att.check_in || "-"}</span>
                          <span style={{ color: "#ef4444" }}>Out: {att.check_out || "-"}</span>
                        </div>
                      )}
                    </td>

                    {/* In Addr */}
                    <td style={tdStyle}>
                      {att.check_in_latitude && att.check_in_longitude ? (
                        <button onClick={() => openMapModal(att.check_in_latitude, att.check_in_longitude)} style={{ color: theme.colors.primary, textDecoration: "underline", background: "none", border: "none", cursor: "pointer", fontSize: "0.7rem" }}>
                          View
                        </button>
                      ) : "-"}
                    </td>

                    {/* Out Addr */}
                    <td style={tdStyle}>
                      {att.check_out_latitude && att.check_out_longitude ? (
                        <button onClick={() => openMapModal(att.check_out_latitude, att.check_out_longitude)} style={{ color: theme.colors.primary, textDecoration: "underline", background: "none", border: "none", cursor: "pointer", fontSize: "0.7rem" }}>
                          View
                        </button>
                      ) : "-"}
                    </td>

                    {/* Photo */}
                    <td style={tdStyle}>
                      {att.check_in_photo || att.check_out_photo ? (
                        <div style={{ display: "flex", gap: "4px" }}>
                          {att.check_in_photo && (
                            <button 
                              onClick={() => openPhotoModal(att.check_in_photo)} 
                              style={{ color: theme.colors.primary, textDecoration: "underline", background: "none", border: "none", cursor: "pointer", fontSize: "0.7rem" }}
                            >
                              In
                            </button>
                          )}
                          {att.check_out_photo && (
                            <button 
                              onClick={() => openPhotoModal(att.check_out_photo)} 
                              style={{ color: theme.colors.primary, textDecoration: "underline", background: "none", border: "none", cursor: "pointer", fontSize: "0.7rem" }}
                            >
                              Out
                            </button>
                          )}
                        </div>
                      ) : "-"}
                    </td>

                    {/* Status */}
                    <td style={tdStyle}>
                      <span style={getStatusStyle(att.attendance_status)}>
                        {att.attendance_status || "-"}
                      </span>
                    </td>

                    <td style={tdStyle}>{att.remarks || "-"}</td>
                    <td style={tdStyle}>
                      <button
                        onClick={() => handleDeleteClick(att.id)}
                        style={{ color: "red", background: "none", border: "none", cursor: "pointer", fontSize: "0.7rem" }}
                      >
                        Del
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: theme.spacing.md, backgroundColor: theme.colors.grayLight }}>
          <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} style={paginationButtonStyle}>Previous</button>
          <p style={{ margin: 0 }}>Page {pagination?.currentPage || 1} of {pagination?.totalPages || 1}</p>
          <button onClick={() => setCurrentPage((p) => Math.min((pagination?.totalPages || 1), p + 1))} disabled={currentPage === pagination?.totalPages} style={paginationButtonStyle}>Next</button>
        </div>
      </div>

      {/* Modals */}
      {photoModalOpen && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 100,
        }}>
          <div style={{ backgroundColor: "#fff", padding: theme.spacing.lg, borderRadius: theme.radius.xl, position: "relative" }}>
            <button onClick={closePhotoModal} style={{ position: "absolute", top: 10, right: 10, background: "none", border: "none", fontSize: "1.25rem", cursor: "pointer" }}>✖</button>
            <img src={photoUrl} alt="Attendance" style={{ maxWidth: "100%", maxHeight: "80vh", borderRadius: theme.radius.md }} />
          </div>
        </div>
      )}

      {mapModalOpen && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 100,
        }}>
          <div style={{ backgroundColor: "#fff", padding: theme.spacing.lg, borderRadius: theme.radius.xl, position: "relative", width: "90%", maxWidth: 500 }}>
            <button onClick={closeMapModal} style={{ position: "absolute", top: 10, right: 10, background: "none", border: "none", fontSize: "1.25rem", cursor: "pointer" }}>✖</button>
            <p>{mapLoading ? "Fetching address..." : mapAddress}</p>
          </div>
        </div>
      )}

      {confirmDeleteOpen && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 101,
        }}>
          <div style={{
            backgroundColor: "#fff", padding: theme.spacing.lg,
            borderRadius: theme.radius.xl, width: "90%", maxWidth: 400, textAlign: "center"
          }}>
            <p style={{ marginBottom: theme.spacing.md }}>Are you sure you want to delete this attendance?</p>
            <div style={{ display: "flex", justifyContent: "space-around" }}>
              <button
                onClick={confirmDelete}
                style={{ backgroundColor: "red", color: "#fff", padding: theme.spacing.sm, borderRadius: theme.radius.md }}
              >
                Yes
              </button>
              <button
                onClick={() => setConfirmDeleteOpen(false)}
                style={{ backgroundColor: "#ccc", color: "#000", padding: theme.spacing.sm, borderRadius: theme.radius.md }}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .spinner {
          width: 40px; height: 40px;
          border: 3px solid #e0e0e0;
          border-top-color: #007bff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        .spinner-small {
          width: 16px; height: 16px;
          border: 2px solid #ffffff;
          border-top-color: transparent;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default Attendance;